import { Button, Form, FormInstance } from 'antd';
import CollapsibleCard from 'components/CollapsibleCard';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADEME_VEHICLE_NOT_FOUND_VALUE,
  CategorieVehicule,
  CategorieVehiculeBus,
  CategorieVehiculeLourd,
  ClientType,
  NouveauDossierConcessionnaireData,
  RAI,
  UsageVehiculeParticulier,
  VehiculeType,
} from 'types/Concessionnaire';
import { getDecile } from 'views/dossiers/concessionnaire/steps/personnePhysiqueFiscal';
import Step2CommandeSectionFields from 'views/dossiers/concessionnaire/steps/step2/Step2CommandeSectionFields';
import Step2QuestionsSectionFields from 'views/dossiers/concessionnaire/steps/step2/Step2QuestionsSectionFields';
import {
  getCommandeSectionFields,
  getQuestionsSectionFields,
  isBus,
  isPersonnePhysique,
  isVehiculeLourd,
  isVulTraEq114Category,
  shouldAskPersonneMoraleFabricationEuropeQuestion,
  shouldShowGrosRouleurQuestion,
} from 'views/dossiers/concessionnaire/steps/step2/step2.selectors';
import useStep2PersonnePhysiqueFlow from 'views/dossiers/concessionnaire/steps/step2/useStep2PersonnePhysiqueFlow';
import useStep2SectionSubmittable from 'views/dossiers/concessionnaire/steps/step2/useStep2SectionSubmittable';

interface Props {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  rai?: RAI;
  // Deprecated: navigation/actions are handled by MultiStepModal footer.
  goToPreviousStep?: () => void;
  isLoading?: boolean;
  error?: Error | null;
  showActions?: boolean;
  showError?: boolean;
  onSubmitAvailabilityChange?: (isSubmittable: boolean) => void;
  onDevisChange?: (files?: NouveauDossierConcessionnaireData['devis']) => void;
  onOtherFilesChange?: (
    files?: NouveauDossierConcessionnaireData['otherFiles'],
  ) => void;
}

const PERSONNE_MORALE_STEP2_FIELDS_COMMON: (keyof NouveauDossierConcessionnaireData)[] =
  [
    'nbDevis',
    'devis',
    'categorieVehicule',
    'nbVehicules',
    'usageVehicules',
    'dateLivraison',
  ];
const STEP2_ALWAYS_REQUIRED_FIELDS: (keyof NouveauDossierConcessionnaireData)[] =
  ['dateLivraison'];

function getPersonneMoraleStep2Fields(
  rai?: RAI,
  vehiculeType?: VehiculeType,
  categorieVehicule?:
    CategorieVehicule | CategorieVehiculeBus | CategorieVehiculeLourd,
) {
  const dateField: keyof NouveauDossierConcessionnaireData =
    rai === '3.1' ? 'dateDevis' : 'dateEditionDevis';
  const fabricationEuropeFields =
    shouldAskPersonneMoraleFabricationEuropeQuestion(vehiculeType) &&
    !isVulTraEq114Category(categorieVehicule)
      ? (['produitEnEurope'] as (keyof NouveauDossierConcessionnaireData)[])
      : [];

  if (rai === '3.2') {
    return [
      ...PERSONNE_MORALE_STEP2_FIELDS_COMMON,
      ...fabricationEuropeFields,
      dateField,
      'datePrevisionnelleDevis',
    ] as (keyof NouveauDossierConcessionnaireData)[];
  }

  return [
    ...PERSONNE_MORALE_STEP2_FIELDS_COMMON,
    ...fabricationEuropeFields,
    dateField,
  ] as (keyof NouveauDossierConcessionnaireData)[];
}

function getPersonneMoraleAdemeFields({
  personnePhysique,
  categorieVehicule,
  ademeMarque,
  ademeModele,
  ademeVehicleNotFound,
}: {
  personnePhysique: boolean;
  categorieVehicule?:
    CategorieVehicule | CategorieVehiculeBus | CategorieVehiculeLourd;
  ademeMarque?: string;
  ademeModele?: string;
  ademeVehicleNotFound?: boolean;
}) {
  if (personnePhysique || !isVulTraEq114Category(categorieVehicule)) {
    return [] as (keyof NouveauDossierConcessionnaireData)[];
  }

  const fields: (keyof NouveauDossierConcessionnaireData)[] = ['ademeMarque'];

  if (
    !ademeVehicleNotFound &&
    ademeMarque &&
    ademeMarque !== ADEME_VEHICLE_NOT_FOUND_VALUE
  ) {
    fields.push('ademeModele');
  }

  if (
    !ademeVehicleNotFound &&
    ademeModele &&
    ademeModele !== ADEME_VEHICLE_NOT_FOUND_VALUE
  ) {
    fields.push('ademeTypeVariante');
  }

  return fields;
}

function isFilledValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function areFieldsFilled(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  return fields.every((field) => isFilledValue(form.getFieldValue(field)));
}

function areFieldsValid(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  const hasErrors = form
    .getFieldsError(fields)
    .some((fieldState) => fieldState.errors.length > 0);
  const hasValidating = fields.some((field) => form.isFieldValidating(field));

  return !hasErrors && !hasValidating;
}

function isSectionSubmittable(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  return areFieldsFilled(form, fields) && areFieldsValid(form, fields);
}

function getFilledFieldNames(
  form: FormInstance<NouveauDossierConcessionnaireData>,
) {
  return Object.keys(form.getFieldsValue()).filter((key) => {
    const value = form.getFieldValue(
      key as keyof NouveauDossierConcessionnaireData,
    );

    return Array.isArray(value) ? value.length > 0 : !!value;
  });
}

export default function Step2({
  form,
  rai,
  onSubmitAvailabilityChange,
  onDevisChange,
  onOtherFilesChange,
}: Props) {
  const { t } = useTranslation();

  const typeClient = form.getFieldValue('typeClientFinal') as
    ClientType | undefined;
  const vehiculeType = form.getFieldValue('vehiculeType') as
    VehiculeType | undefined;

  const personnePhysique = isPersonnePhysique(typeClient);
  const isVehiculeLourdValue = isVehiculeLourd(vehiculeType);
  const isBusValue = isBus(vehiculeType);

  const watchedCategorieVehicule = Form.useWatch('categorieVehicule', form) as
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd
    | undefined;
  const categorieVehicule =
    watchedCategorieVehicule ??
    (form.getFieldValue('categorieVehicule') as
      | CategorieVehicule
      | CategorieVehiculeBus
      | CategorieVehiculeLourd
      | undefined);
  const watchedUsageVehicule = Form.useWatch(
    'usageVehiculeParticulier',
    form,
  ) as UsageVehiculeParticulier | undefined;
  const usageVehicule =
    watchedUsageVehicule ??
    (form.getFieldValue('usageVehiculeParticulier') as
      UsageVehiculeParticulier | undefined);
  const ademeMarque = Form.useWatch('ademeMarque', form) as string | undefined;
  const ademeModele = Form.useWatch('ademeModele', form) as string | undefined;
  const ademeVehicleNotFound = Form.useWatch('ademeVehicleNotFound', form) as
    boolean | undefined;
  const watchedDatePrevisionnelleDevis = Form.useWatch(
    'datePrevisionnelleDevis',
    form,
  );
  const datePrevisionnelleDevis =
    watchedDatePrevisionnelleDevis ??
    form.getFieldValue('datePrevisionnelleDevis');
  const watchedDateLivraison = Form.useWatch('dateLivraison', form);
  const dateLivraison =
    watchedDateLivraison ?? form.getFieldValue('dateLivraison');
  const watchedDecile = Form.useWatch('decile', form);
  const decile = watchedDecile ?? form.getFieldValue('decile');
  const watchedScoreEnvironnementalAtteint = Form.useWatch(
    'scoreEnvironnementalAtteint',
    form,
  );
  const scoreEnvironnementalAtteint =
    watchedScoreEnvironnementalAtteint ??
    form.getFieldValue('scoreEnvironnementalAtteint');
  const watchedCoutAcquisitionInferieur47000 = Form.useWatch(
    'coutAcquisitionInferieur47000',
    form,
  );
  const coutAcquisitionInferieur47000 =
    watchedCoutAcquisitionInferieur47000 ??
    form.getFieldValue('coutAcquisitionInferieur47000');
  const watchedMasseVehiculeInferieure2400 = Form.useWatch(
    'masseVehiculeInferieure2400',
    form,
  );
  const masseVehiculeInferieure2400 =
    watchedMasseVehiculeInferieure2400 ??
    form.getFieldValue('masseVehiculeInferieure2400');
  const watchedDejaBeneficieBonusEcologique = Form.useWatch(
    'dejaBeneficieBonusEcologique',
    form,
  );
  const dejaBeneficieBonusEcologique =
    watchedDejaBeneficieBonusEcologique ??
    form.getFieldValue('dejaBeneficieBonusEcologique');
  const watchedProduitEnEurope = Form.useWatch('produitEnEurope', form);
  const produitEnEurope =
    watchedProduitEnEurope ?? form.getFieldValue('produitEnEurope');
  const watchedVehiculeAdeme = Form.useWatch('vehiculeAdeme', form);
  const vehiculeAdeme =
    watchedVehiculeAdeme ?? form.getFieldValue('vehiculeAdeme');
  const revenuFiscalReference =
    Form.useWatch('revenuFiscalReference', form) ??
    form.getFieldValue('revenuFiscalReference');
  const nombreParts =
    Form.useWatch('nombreParts', form) ?? form.getFieldValue('nombreParts');
  const effectiveDecile =
    decile ??
    form.getFieldValue('decile') ??
    getDecile(revenuFiscalReference, nombreParts);

  const personnePhysiqueGrosRouleurValues = useMemo(
    () => ({
      datePrevisionnelleDevis,
      dateLivraison,
      decile: effectiveDecile,
      scoreEnvironnementalAtteint,
      coutAcquisitionInferieur47000,
      masseVehiculeInferieure2400,
      dejaBeneficieBonusEcologique,
      produitEnEurope,
      vehiculeAdeme,
    }),
    [
      coutAcquisitionInferieur47000,
      dateLivraison,
      datePrevisionnelleDevis,
      dejaBeneficieBonusEcologique,
      effectiveDecile,
      masseVehiculeInferieure2400,
      produitEnEurope,
      scoreEnvironnementalAtteint,
      vehiculeAdeme,
    ],
  );

  const commandeSectionFields = useMemo(
    () => getCommandeSectionFields(personnePhysique),
    [personnePhysique],
  );
  const questionsSectionFields = useMemo(
    () =>
      getQuestionsSectionFields({
        personnePhysique,
        categorieVehicule,
        usageVehicule,
        values: personnePhysiqueGrosRouleurValues,
      }),
    [
      categorieVehicule,
      personnePhysique,
      personnePhysiqueGrosRouleurValues,
      usageVehicule,
    ],
  );
  const showGrosRouleurQuestion = shouldShowGrosRouleurQuestion({
    personnePhysique,
    categorieVehicule,
    usageVehicule,
    values: personnePhysiqueGrosRouleurValues,
  });
  const personneMoraleStep2Fields = useMemo(
    () => [
      ...getPersonneMoraleStep2Fields(rai, vehiculeType, categorieVehicule),
      ...getPersonneMoraleAdemeFields({
        personnePhysique,
        categorieVehicule,
        ademeMarque,
        ademeModele,
        ademeVehicleNotFound,
      }),
    ],
    [
      ademeMarque,
      ademeModele,
      ademeVehicleNotFound,
      categorieVehicule,
      personnePhysique,
      rai,
      vehiculeType,
    ],
  );
  const fieldsToValidateForStep2 = useMemo(
    () =>
      personnePhysique
        ? [...commandeSectionFields, ...questionsSectionFields]
        : personneMoraleStep2Fields,
    [
      commandeSectionFields,
      personneMoraleStep2Fields,
      personnePhysique,
      questionsSectionFields,
    ],
  );
  const uniqueStep2Fields = useMemo(
    () =>
      Array.from(
        new Set([...fieldsToValidateForStep2, ...STEP2_ALWAYS_REQUIRED_FIELDS]),
      ),
    [fieldsToValidateForStep2],
  );
  const isFormValid = useStep2SectionSubmittable({
    form,
    fields: uniqueStep2Fields,
  });

  const flow = useStep2PersonnePhysiqueFlow({
    form,
    personnePhysique,
    commandeSectionFields,
    questionsSectionFields,
  });

  const isStepSubmittable = personnePhysique
    ? flow.isCommandeSectionSaved && flow.isQuestionsSectionSaved
    : isFormValid;
  const hasValidatedFilledFieldsRef = useRef(false);

  // (Re)Validation des champs remplis.
  // Dans le cas ou l'utilisateur clique sur "Precedent" puis "Suivant".
  // Et synchronisation de l'état "submittable" vers le parent.
  useEffect(() => {
    if (!hasValidatedFilledFieldsRef.current) {
      hasValidatedFilledFieldsRef.current = true;
      form.validateFields(getFilledFieldNames(form)).catch(() => undefined);
    }

    onSubmitAvailabilityChange?.(isStepSubmittable);
  }, [form, isStepSubmittable, onSubmitAvailabilityChange]);

  return (
    <Form
      form={form}
      name="step2"
      layout="vertical"
      className="[&_label]:font-bold [&_p]:font-bold"
      onValuesChange={flow.handleValuesChange}
    >
      {personnePhysique ? (
        <div className="space-y-4">
          <CollapsibleCard
            title={t('nouveau_formulaire:bon_commande_section_title')}
            isOpen={flow.isCommandeSectionOpen}
            isComplete={flow.isCommandeSectionSaved}
            onToggle={
              flow.isCommandeSectionSaved
                ? flow.toggleCommandeSection
                : undefined
            }
          >
            <div className="space-y-4 mt-4">
              <Step2CommandeSectionFields
                form={form}
                rai={rai}
                personnePhysique={personnePhysique}
                onDevisChange={onDevisChange}
                onOtherFilesChange={onOtherFilesChange}
              />
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    htmlType="button"
                    onClick={flow.handleSaveCommandeSection}
                    shape="round"
                    disabled={
                      !isSectionSubmittable(form, commandeSectionFields)
                    }
                  >
                    {t('global:enregistrer_et_continuer')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            title={t(
              'nouveau_formulaire:questions_achat_vehicules_neufs_section_title',
            )}
            isOpen={flow.isQuestionsSectionOpen}
            showContent={flow.isCommandeSectionSaved}
            isComplete={flow.isQuestionsSectionSaved}
            onToggle={
              flow.isQuestionsSectionSaved
                ? flow.toggleQuestionsSection
                : undefined
            }
          >
            <div className="space-y-4 mt-4">
              <Step2QuestionsSectionFields
                form={form}
                personnePhysique={personnePhysique}
                vehiculeType={vehiculeType}
                isVehiculeLourd={isVehiculeLourdValue}
                isBus={isBusValue}
                categorieVehicule={categorieVehicule}
                usageVehicule={usageVehicule}
                showGrosRouleurQuestion={showGrosRouleurQuestion}
              />
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    htmlType="button"
                    onClick={flow.handleSaveQuestionsSection}
                    shape="round"
                    disabled={
                      !isSectionSubmittable(form, questionsSectionFields)
                    }
                  >
                    {t('global:enregistrer_et_continuer')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </CollapsibleCard>
        </div>
      ) : (
        <>
          <Step2CommandeSectionFields
            form={form}
            rai={rai}
            personnePhysique={personnePhysique}
            onDevisChange={onDevisChange}
            onOtherFilesChange={onOtherFilesChange}
          />
          <Step2QuestionsSectionFields
            form={form}
            personnePhysique={personnePhysique}
            vehiculeType={vehiculeType}
            isVehiculeLourd={isVehiculeLourdValue}
            isBus={isBusValue}
            categorieVehicule={categorieVehicule}
            usageVehicule={usageVehicule}
            showGrosRouleurQuestion={showGrosRouleurQuestion}
          />
        </>
      )}
    </Form>
  );
}
